import { Autocomplete, Box, FormControl, FormHelperText, MenuItem, TextField } from '@mui/material';
import { styled } from '@mui/material/styles';
import defaultPartnerImage from 'assets/img/partners/default-partner-image.png';
import Image from 'DesignSystem/DataDisplay/Image/Image';
import PaperWithAddItem from 'DesignSystem/Inputs/Paper/Paper';
import Icon from 'DesignSystem/Primitives/Icon/Icon';
import { useCompanyPlanContext } from 'FeatureModules/Auth/Contexts/CompanyPlanContext';
import { ECompanyStatus } from 'FeatureModules/Auth/types/company.types';
import usePartners from 'FeatureModules/Partner/Components/hooks/usePartners';
import { IBasePartnerCompany } from 'FeatureModules/Partner/types/partner.types';
import useDialog from 'hooks/useDialog';
import React, { ChangeEvent, FC, useCallback, useEffect, useMemo, useState } from 'react';

interface IPartnerAutocompleteProps {
  inviteNewPartner?: boolean;
  defaultValue?: IBasePartnerCompany | null;
  placeholder?: string;
  /* Default is true */
  filterByRegisteredPartners?: boolean;
  excludeOwnCompany?: boolean;
  size?: 'small' | 'medium';
  disabled?: boolean;
  fontSize?: number;
  hasError?: boolean;
  errorMessage?: string;
  disablePartnerCreation?: boolean;
  onPartnerSelect: (partner: IBasePartnerCompany | null) => void;
  'data-testid'?: string;
}

const StyledAutocomplete = styled(Autocomplete<IBasePartnerCompany>)<{ fontSize?: number }>(
  ({ fontSize }) => ({
    '& .MuiAutocomplete-inputRoot': {
      paddingLeft: '16px !important',
      fontSize: fontSize ? fontSize : 12,
    },
    '& .MuiAutocomplete-inputRoot[class*="MuiOutlinedInput-root"]': {
      padding: 4,
      '& .MuiInputBase-input': {
        fontSize: fontSize ? fontSize : 12,
      },
    },
    '& .MuiAutocomplete-inputRoot[class*="MuiOutlinedInput-root"][class*="MuiOutlinedInput-marginDense"]':
      {
        padding: 4,
        '& .MuiInputBase-input': {
          fontSize: fontSize ? fontSize : 12,
        },
      },
  })
);

const StyledFormHelperText = styled(FormHelperText)(() => ({
  '&.MuiFormHelperText-root': {
    marginTop: -2,
  },
}));

const PartnerAutocomplete: FC<IPartnerAutocompleteProps> = ({
  inviteNewPartner,
  defaultValue = null,
  placeholder = 'Select owner',
  size = 'medium',
  filterByRegisteredPartners = true,
  excludeOwnCompany,
  disablePartnerCreation,
  disabled,
  fontSize,
  hasError,
  errorMessage = 'Please select an owner',
  onPartnerSelect,
  'data-testid': testId,
}) => {
  const { openDialog } = useDialog();
  const [selectPartnerId, setSelectPartnerId] = useState<string | null>(null);
  const [selectedPartner, setSelectedPartner] = useState<IBasePartnerCompany | null>(defaultValue);

  const { company } = useCompanyPlanContext();
  const { partners, loading, refetch } = usePartners({ skip: disabled });
  const sizeImage = size === 'small' ? 14 : 17;

  const partnerOptions: IBasePartnerCompany[] = useMemo(() => {
    if (filterByRegisteredPartners) {
      return [
        ...partners.filter(partner => partner.status === ECompanyStatus.REGISTERED),
        ...(excludeOwnCompany ? [] : [company]),
      ];
    }
    return [...partners, ...(excludeOwnCompany ? [] : [company])];
  }, [partners, company, filterByRegisteredPartners, excludeOwnCompany]);

  const handleSelectPartner = (
    event: ChangeEvent<unknown>,
    partner: IBasePartnerCompany | null
  ) => {
    setSelectedPartner(partner);
    onPartnerSelect(partner);
  };

  const handleNewPartnerCreated = useCallback(
    async (partner: IBasePartnerCompany) => {
      await refetch();
      setSelectPartnerId(partner.id);
    },
    [refetch, setSelectPartnerId]
  );

  const handleCreatePartner = () => {
    openDialog({
      type: 'ADD_EDIT_PARTNER',
      props: {
        enableInvite: inviteNewPartner,
        skipInvitationEmail: true,
        onPartnerCreated: handleNewPartnerCreated,
      },
    });
  };

  /**
   * Waiting until the partner list got updated and select the correct partner as soon as it exists
   * Removing the selectedPartner from the temporary state variable after the partner got created
   */
  useEffect(() => {
    if (selectPartnerId) {
      const partner = partners.find(({ id }) => id === selectPartnerId);
      if (!partner) {
        // eslint-disable-next-line no-console
        console.error(`Partner with id ${selectPartnerId} not found`);
        setSelectPartnerId(null);
        return;
      }
      setSelectedPartner(partner);
      onPartnerSelect(partner);
      setSelectPartnerId(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectPartnerId, partners]);

  /** Listen to the changes */
  useEffect(() => setSelectedPartner(defaultValue), [defaultValue]);

  return (
    <FormControl fullWidth size={size} error={hasError}>
      <StyledAutocomplete
        loading={loading}
        value={selectedPartner}
        options={partnerOptions}
        data-testid={testId}
        onChange={handleSelectPartner}
        fullWidth
        fontSize={fontSize}
        size={size}
        disabled={disabled}
        PaperComponent={({ children }) => (
          <PaperWithAddItem
            ActionComponent={
              !disablePartnerCreation ? (
                <MenuItem onMouseDown={handleCreatePartner}>
                  <Icon name="plus" mr={1} />
                  Add new partner
                </MenuItem>
              ) : undefined
            }
          >
            {children}
          </PaperWithAddItem>
        )}
        getOptionLabel={option => option.name}
        renderOption={(props, option: IBasePartnerCompany) => (
          <li {...props} key={option.id} data-testid={`${testId}-option`}>
            <Box mr={2} display="flex" alignItems="center">
              <Image
                width={sizeImage}
                height={sizeImage}
                image={option.logo}
                backupImageUrl={defaultPartnerImage}
                borderRadius={0}
              />
            </Box>
            {option.name}
          </li>
        )}
        renderInput={params => (
          <TextField
            {...params}
            error={hasError}
            variant="outlined"
            placeholder={placeholder}
            onKeyPress={keyEvent => {
              // Prevents the form from being submitted on enter.
              if (keyEvent.key === 'Enter') {
                keyEvent.preventDefault();
              }
            }}
            InputProps={{
              style: { height: size === 'medium' ? 40 : 30 },
              ...params.InputProps,
              startAdornment: selectedPartner ? (
                <Image
                  width={sizeImage}
                  height={sizeImage}
                  image={selectedPartner.logo}
                  backupImageUrl={defaultPartnerImage}
                  borderRadius={0}
                />
              ) : (
                <Image
                  width={sizeImage}
                  height={sizeImage}
                  backupImageUrl={defaultPartnerImage}
                  borderRadius={0}
                />
              ),
            }}
          />
        )}
      />
      {hasError && <StyledFormHelperText>{errorMessage}</StyledFormHelperText>}
    </FormControl>
  );
};

export default PartnerAutocomplete;
